import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { PolicyApi } from '@Apis';
import { useGlobalSettingStore } from 'src/models/useGlobalSettingModel';
import ExchangeOnlinePolicy from '../PolicyTemplate/Email/ExchangeOnline';
import { useOrgStore } from 'src/models/useOrgModel';
import { useLoginUserStore } from 'src/models/useLoginUserModel';
import merge from 'lodash/merge';
import { PolicyType, PolicyType2Service } from '../../../../types/app';
import { PolicySubTypeEnum } from '../../../../types';
import DlpExchangeOnlineOutboundPolicy from '../PolicyTemplate/Email/ExchangeOnlineOutbound';
import DlpExchangeOnlineInboundPolicy from '../PolicyTemplate/Email/ExchangeOnlineInbound';
import DlpGmailPolicy from '../PolicyTemplate/Email/Gmail';
import { getDlpExchangeOutboundTemplateConfig } from '../../TemplateConfig/cfg.dlp.exchangeOutbound';
import { getDlpGmailTemplateConfig } from '../../TemplateConfig/cfg.dlp.gmail';
import {
  getDlpConfig,
  getDlpExchangeTemplateConfig,
} from '../../TemplateConfig/cfg.dlp.exchange';
import { getDlpGmailInboundTemplateConfig } from '../../TemplateConfig/cfg.dlp.gmailInbound';
import DlpGmailInboundPolicy from '../PolicyTemplate/Email/GmailInlineInbound';
import DlpGmailOutboundPolicy from '../PolicyTemplate/Email/GmailInlineOutbound';
import { getDlpGmailOutboundTemplateConfig } from '../../TemplateConfig/cfg.dlp.gmailOutbound';
import DlpTeamsPolicy from '../PolicyTemplate/Collaboration/Teams';
import DlpTeamsChatPolicy from '../PolicyTemplate/Collaboration/TeamsChat';
import { getDlpOneDriveTemplateConfig } from '../../TemplateConfig/cfg.dlp.oneDrive';
import DlpOneDrivePolicy from '../PolicyTemplate/Collaboration/OneDrive';
import { getDlpSharePointTemplateConfig } from '../../TemplateConfig/cfg.dlp.sharePoint';
import DlpSharePointPolicy from '../PolicyTemplate/Collaboration/SharePoint';
import DlpGoogleDrivePolicy from '../PolicyTemplate/Collaboration/GoogleDrive';
import { getDlpTeamsTemplateConfig } from '../../TemplateConfig/cfg.dlp.teams';
import { getDlpTeamsChatTemplateConfig } from '../../TemplateConfig/cfg.dlp.teamsChat';
import { getDlpGoogleDriveTemplateConfig } from '@Policy/TemplateConfig/cfg.googleDrive.dlp';
import DlpDropboxPolicy from '../PolicyTemplate/Collaboration/Dropbox';
import { getDlpDropboxTemplateConfig } from '@Policy/TemplateConfig/cfg.dropbox.dlp';
import DlpBoxPolicy from '@Policy/DataLossPrevention/PolicyTemplate/Collaboration/Box';
import { getDlpBoxTemplateConfig } from '@Policy/TemplateConfig/cfg.box.dlp';
import DlpSalesforceSandbox from '../PolicyTemplate/Collaboration/SalesforceSandbox';
import { getDlpSalesforceSandboxTemplateConfig } from '@Policy/TemplateConfig/cfg.dlp.salesforceSandbox';
import { isInUIC } from '@Utils/common';
import { useBreadCrumbStore } from '@Models/useBreadCrumbModel';
import DlpSalesforceProduction from '../PolicyTemplate/Collaboration/SalesforceProduction';
import { getDlpSalesforceProductionTemplateConfig } from '@Policy/TemplateConfig/cfg.dlp.salesforceProduction';
import { replaceProductionNameInPolicyEntity } from '../../utils/fns';
import { ApplyBoundEnum } from '../../utils/types';
import CSpinner from '@Components/CSpinner';
import PageNotFound from '@Components/Common/NoPermission';

const EditDlpPolicies = () => {
  const { policyUuid } = useParams();
  const { t } = useTranslation();
  const loginUserModel = useLoginUserStore();
  const globalSettingModule = useGlobalSettingStore();
  const { orgId, switchOrgId } = useOrgStore();
  const { selectedOrgGlobalSetting } = globalSettingModule;
  const mipFeatureShow = !!selectedOrgGlobalSetting?.mipFeatureShow;
  const [isLoading, setIsLoading] = useState(false);
  const [policyData, setPolicyData] = useState<any>(null);
  const breadCrumbModal = useBreadCrumbStore();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        let policy = (await PolicyApi.getPolicyById(policyUuid as string)).data;
        const { companyId } = policy;
        if (orgId !== companyId) {
          switchOrgId(companyId);
        }

        policy = replaceProductionNameInPolicyEntity(policy);
        setPolicyData(policy);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [orgId, policyUuid, switchOrgId]);

  const updateBreadcrumb = useCallback(
    (policyType: PolicyType, policySubType: PolicySubTypeEnum) => {
      let serviceName = '';
      if (PolicyType.ExchangeInline === policyType) {
        serviceName =
          policySubType === PolicySubTypeEnum.inbound
            ? t('common.sideTitleService.exchangeInlineInbound')
            : t('common.sideTitleService.exchangeInlineOutbound');
      } else if (PolicyType.GmailInline === policyType) {
        serviceName =
          policySubType === PolicySubTypeEnum.inbound
            ? t('common.sideTitleService.gmailInlineInbound')
            : t('common.sideTitleService.gmailInlineOutbound');
      } else {
        serviceName = t(
          `common.service.${PolicyType2Service[policyType as PolicyType]}`
        );
      }
      if (isInUIC()) {
        breadCrumbModal.updateBreadCrumb([
          {
            breadCrumb: t('common.nativeApp.productName'),
            router: '',
          },
          {
            breadCrumb: t('side.group.policy'),
            router: '',
          },
          {
            breadCrumb: t('side.label.addDLPPolicy', {
              service: serviceName,
            }),
            router: '',
          },
        ]);
      } else {
        breadCrumbModal.updateBreadCrumb([
          {
            breadCrumb: t('side.group.policy'),
            router: '',
          },
          {
            breadCrumb: t('side.label.addDLPPolicy', {
              service: serviceName,
            }),
            router: '',
          },
        ]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t]
  );

  const policyComponent = useMemo(() => {
    if (!policyData) return null;

    let configData: any = {};
    let defaultTemplate: any = {};
    let templateData: any = {};

    const { policyType, policySubType, companyId } = policyData;

    updateBreadcrumb(policyType, policySubType);

    switch (policyType) {
      case PolicyType.Exchange:
        defaultTemplate = getDlpExchangeTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getDlpConfig(t, selectedOrgGlobalSetting, defaultTemplate);
        configData.exchangeOnlineApplyBoundOptions = [
          ApplyBoundEnum.sentMessageOnly,
          ApplyBoundEnum.incomming,
          ApplyBoundEnum.all,
        ];
        templateData = merge(defaultTemplate, policyData);
        // remove policyTarget.empty, this property is only for addPolicy frontend
        if (templateData.policyTarget?.empty) {
          templateData.policyTarget.empty = undefined;
        }
        return (
          <ExchangeOnlinePolicy
            template={templateData}
            config={configData}
            orgId={companyId}
          />
        );
      case PolicyType.ExchangeInline:
        if (policySubType === PolicySubTypeEnum.inbound) {
          defaultTemplate = getDlpExchangeOutboundTemplateConfig(
            t,
            loginUserModel.user?.accountEmail
          );
          configData = getDlpConfig(
            t,
            selectedOrgGlobalSetting,
            defaultTemplate
          );
          templateData = merge(defaultTemplate, policyData);
          // remove policyTarget.empty, this property is only for addPolicy frontend
          if (templateData.policyTarget?.empty) {
            templateData.policyTarget.empty = undefined;
          }
          console.log(templateData);
          return (
            <DlpExchangeOnlineInboundPolicy
              template={templateData}
              config={configData}
              orgId={companyId}
            />
          );
        } else if (policySubType === PolicySubTypeEnum.outbound) {
          defaultTemplate = getDlpExchangeOutboundTemplateConfig(
            t,
            loginUserModel.user?.accountEmail
          );
          configData = getDlpConfig(
            t,
            selectedOrgGlobalSetting,
            defaultTemplate
          );
          templateData = merge(defaultTemplate, policyData);
          // remove policyTarget.empty, this property is only for addPolicy frontend
          if (templateData.policyTarget?.empty) {
            templateData.policyTarget.empty = undefined;
          }
          return (
            <DlpExchangeOnlineOutboundPolicy
              template={templateData}
              config={configData}
              orgId={companyId}
            />
          );
        }
        break;
      case PolicyType.OneDrive:
        defaultTemplate = getDlpOneDriveTemplateConfig(
          t,
          loginUserModel.user?.accountEmail,
          mipFeatureShow
        );
        configData = getDlpConfig(t, selectedOrgGlobalSetting, defaultTemplate);
        templateData = merge(defaultTemplate, policyData);
        // remove policyTarget.empty, this property is only for addPolicy frontend
        if (templateData.policyTarget?.empty) {
          templateData.policyTarget.empty = undefined;
        }
        return (
          <DlpOneDrivePolicy
            template={templateData}
            config={configData}
            orgId={companyId}
          />
        );
      case PolicyType.SharePoint:
        defaultTemplate = getDlpSharePointTemplateConfig(
          t,
          loginUserModel.user?.accountEmail,
          mipFeatureShow
        );
        configData = getDlpConfig(t, selectedOrgGlobalSetting, defaultTemplate);
        templateData = merge(defaultTemplate, policyData);
        // remove policyTarget.empty, this property is only for addPolicy frontend
        if (templateData.policyTarget?.empty) {
          templateData.policyTarget.empty = undefined;
        }
        return (
          <DlpSharePointPolicy
            template={templateData}
            config={configData}
            orgId={companyId}
          />
        );
      case PolicyType.Gmail:
        defaultTemplate = getDlpGmailTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getDlpConfig(t, selectedOrgGlobalSetting, defaultTemplate);
        templateData = merge(defaultTemplate, policyData);
        // remove policyTarget.empty, this property is only for addPolicy frontend
        if (templateData.policyTarget?.empty) {
          templateData.policyTarget.empty = undefined;
        }
        return (
          <DlpGmailPolicy
            template={templateData}
            config={configData}
            orgId={companyId}
          />
        );
      case PolicyType.GmailInline:
        if (policySubType === PolicySubTypeEnum.inbound) {
          defaultTemplate = getDlpGmailInboundTemplateConfig(
            t,
            loginUserModel.user?.accountEmail
          );
          configData = getDlpConfig(
            t,
            selectedOrgGlobalSetting,
            defaultTemplate
          );
          templateData = merge(defaultTemplate, policyData);
          // remove policyTarget.empty, this property is only for addPolicy frontend
          if (templateData.policyTarget?.empty) {
            templateData.policyTarget.empty = undefined;
          }
          return (
            <DlpGmailInboundPolicy
              template={templateData}
              config={configData}
              orgId={companyId}
            />
          );
        } else if (policySubType === PolicySubTypeEnum.outbound) {
          defaultTemplate = getDlpGmailOutboundTemplateConfig(
            t,
            loginUserModel.user?.accountEmail
          );
          configData = getDlpConfig(
            t,
            selectedOrgGlobalSetting,
            defaultTemplate
          );
          templateData = merge(defaultTemplate, policyData);
          // remove policyTarget.empty, this property is only for addPolicy frontend
          if (templateData.policyTarget?.empty) {
            templateData.policyTarget.empty = undefined;
          }
          return (
            <DlpGmailOutboundPolicy
              template={templateData}
              config={configData}
              orgId={companyId}
            />
          );
        }
        break;
      case PolicyType.Teams:
        defaultTemplate = getDlpTeamsTemplateConfig(
          t,
          loginUserModel.user?.accountEmail,
          mipFeatureShow
        );
        configData = getDlpConfig(t, selectedOrgGlobalSetting, defaultTemplate);
        templateData = merge(defaultTemplate, policyData);
        // remove policyTarget.empty, this property is only for addPolicy frontend
        if (templateData.policyTarget?.empty) {
          templateData.policyTarget.empty = undefined;
        }
        return (
          <DlpTeamsPolicy
            template={templateData}
            config={configData}
            orgId={companyId}
          />
        );
      case PolicyType.TeamsChat:
        defaultTemplate = getDlpTeamsChatTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getDlpConfig(t, selectedOrgGlobalSetting, defaultTemplate);
        templateData = merge(defaultTemplate, policyData);
        // remove policyTarget.empty, this property is only for addPolicy frontend
        if (templateData.policyTarget?.empty) {
          templateData.policyTarget.empty = undefined;
        }
        return (
          <DlpTeamsChatPolicy
            template={templateData}
            config={configData}
            orgId={companyId}
          />
        );
      case PolicyType.GoogleDrive:
        defaultTemplate = getDlpGoogleDriveTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getDlpConfig(t, selectedOrgGlobalSetting, defaultTemplate);
        templateData = merge(defaultTemplate, policyData);
        // remove policyTarget.empty, this property is only for addPolicy frontend
        if (templateData.policyTarget?.empty) {
          templateData.policyTarget.empty = undefined;
        }
        return (
          <DlpGoogleDrivePolicy
            template={templateData}
            config={configData}
            orgId={companyId}
          />
        );
      case PolicyType.Dropbox:
        defaultTemplate = getDlpDropboxTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getDlpConfig(t, selectedOrgGlobalSetting, defaultTemplate);
        templateData = merge(defaultTemplate, policyData);
        // remove policyTarget.empty, this property is only for addPolicy frontend
        if (templateData.policyTarget?.empty) {
          templateData.policyTarget.empty = undefined;
        }
        return (
          <DlpDropboxPolicy
            template={templateData}
            config={configData}
            orgId={companyId}
          />
        );
      case PolicyType.Box:
        defaultTemplate = getDlpBoxTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getDlpConfig(t, selectedOrgGlobalSetting, defaultTemplate);
        templateData = merge(defaultTemplate, policyData);
        // remove policyTarget.empty, this property is only for addPolicy frontend
        if (templateData.policyTarget?.empty) {
          templateData.policyTarget.empty = undefined;
        }
        if (templateData.policySubType === 1) {
          // box sharelink policy does not need keywordFilter
          templateData.tmKeywordfilters = undefined;
        }
        return (
          <DlpBoxPolicy
            template={templateData}
            config={configData}
            orgId={companyId}
          />
        );
      case PolicyType.SalesforceSandbox:
        defaultTemplate = getDlpSalesforceSandboxTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getDlpConfig(t, selectedOrgGlobalSetting, defaultTemplate);
        templateData = merge(defaultTemplate, policyData);
        // remove policyTarget.empty, this property is only for addPolicy frontend
        if (templateData.policyTarget?.empty) {
          templateData.policyTarget.empty = undefined;
        }
        if (templateData.policySubType === 1) {
          // box sharelink policy does not need keywordFilter
          templateData.tmKeywordfilters = undefined;
        }
        return (
          <DlpSalesforceSandbox
            template={templateData}
            config={configData}
            orgId={companyId}
          />
        );
      case PolicyType.SalesforceProduction:
        defaultTemplate = getDlpSalesforceProductionTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getDlpConfig(t, selectedOrgGlobalSetting, defaultTemplate);
        templateData = merge(defaultTemplate, policyData);
        // remove policyTarget.empty, this property is only for addPolicy frontend
        if (templateData.policyTarget?.empty) {
          templateData.policyTarget.empty = undefined;
        }
        if (templateData.policySubType === 1) {
          // box sharelink policy does not need keywordFilter
          templateData.tmKeywordfilters = undefined;
        }
        return (
          <DlpSalesforceProduction
            template={templateData}
            config={configData}
            orgId={companyId}
          />
        );
      default:
        return <PageNotFound />;
    }
  }, [
    loginUserModel.user?.accountEmail,
    mipFeatureShow,
    policyData,
    selectedOrgGlobalSetting,
    t,
    updateBreadcrumb,
  ]);

  if (isLoading) return <CSpinner />;
  if (!policyData) return <div>No policy data available</div>;

  return <>{policyComponent}</>;
};

export default EditDlpPolicies;
